import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface List {
  id: string;
  project_id: string;
  title: string;
  created_at: string;
}

interface ListItem {
  id: string;
  list_id: string;
  content: string;
  notes: string | null;
  is_completed: boolean;
  created_at: string;
}

interface ProjectListsProps {
  projectId: string;
}

interface ParsedTable {
  headers: string[];
  rows: string[][];
}

const TABLE_NOTES_PREFIX = '__TABLE_JSON__:';

const serializeTableToNotes = (table: ParsedTable): string => `${TABLE_NOTES_PREFIX}${JSON.stringify(table)}`;

const parseTableFromNotes = (notes: string | null): ParsedTable | null => {
  if (!notes || !notes.startsWith(TABLE_NOTES_PREFIX)) return null;

  try {
    const parsed = JSON.parse(notes.slice(TABLE_NOTES_PREFIX.length));
    if (!parsed || !Array.isArray(parsed.headers) || !Array.isArray(parsed.rows)) return null;
    return parsed as ParsedTable;
  } catch {
    return null;
  }
};

const parseTabularText = (raw: string): ParsedTable | null => {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) return null;

  const matrix = lines.map((line) => line.split('\t').map((cell) => cell.trim()));
  const maxColumns = Math.max(...matrix.map((row) => row.length), 0);

  if (maxColumns === 0) return null;

  const normalized = matrix.map((row) => {
    const padded = [...row];
    while (padded.length < maxColumns) padded.push('');
    return padded;
  });

  return {
    headers: normalized[0],
    rows: normalized.slice(1),
  };
};

export default function ProjectLists({ projectId }: ProjectListsProps) {
  const [lists, setLists] = useState<List[]>([]);
  const [itemsByListId, setItemsByListId] = useState<Record<string, ListItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddList, setShowAddList] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [addingList, setAddingList] = useState(false);
  const [newItemInputs, setNewItemInputs] = useState<Record<string, string>>({});
  const [notePopup, setNotePopup] = useState<{ listId: string; itemId: string; notes: string } | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [tablePopup, setTablePopup] = useState<{ listId: string } | null>(null);
  const [tableTitle, setTableTitle] = useState('Excel Tablosu');
  const [tablePaste, setTablePaste] = useState('');
  const [addingTable, setAddingTable] = useState(false);

  // Fetch lists and items from Supabase
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError('');

      // Fetch lists
      const { data: listsData, error: listsError } = await supabase
        .from('lists')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (listsError) {
        setError('Listeler yüklenirken bir hata oluştu');
        setLoading(false);
        return;
      }

      const fetchedLists = listsData || [];
      setLists(fetchedLists);

      // Fetch items for all lists
      if (fetchedLists.length > 0) {
        const listIds = fetchedLists.map((l) => l.id);
        const { data: itemsData, error: itemsError } = await supabase
          .from('list_items')
          .select('*')
          .in('list_id', listIds)
          .order('created_at', { ascending: true });

        if (itemsError) {
          setError('Kalemler yüklenirken bir hata oluştu');
          setLoading(false);
          return;
        }

        const grouped = (itemsData || []).reduce((acc, item) => {
          if (!acc[item.list_id]) acc[item.list_id] = [];
          acc[item.list_id].push(item);
          return acc;
        }, {} as Record<string, ListItem[]>);
        setItemsByListId(grouped);
      }

      setLoading(false);
    }

    fetchData();
  }, [projectId]);

  const handleAddList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newListTitle.trim()) return;

    setAddingList(true);
    setError('');

    const { data, error: insertError } = await supabase
      .from('lists')
      .insert({
        project_id: projectId,
        title: newListTitle.trim(),
      })
      .select()
      .single();

    if (insertError || !data) {
      setError('Liste oluşturulurken bir hata oluştu');
      setAddingList(false);
      return;
    }

    setLists([...lists, data]);
    setNewListTitle('');
    setShowAddList(false);
    setAddingList(false);
  };

  const handleDeleteList = async (listId: string) => {
    const { error: deleteError } = await supabase
      .from('lists')
      .delete()
      .eq('id', listId);

    if (deleteError) {
      setError('Liste silinirken bir hata oluştu');
      return;
    }

    setLists(lists.filter((l) => l.id !== listId));
    setItemsByListId((prev) => {
      const copy = { ...prev };
      delete copy[listId];
      return copy;
    });
  };

  const handleAddItem = async (listId: string) => {
    const content = newItemInputs[listId]?.trim();
    if (!content) return;

    const { data, error: insertError } = await supabase
      .from('list_items')
      .insert({
        list_id: listId,
        content,
      })
      .select()
      .single();

    if (insertError || !data) {
      console.error('List item insert error:', insertError);
      const errMsg = insertError?.message || insertError?.details || insertError?.hint || 'Bilinmeyen hata';
      setError(`Kalem eklenirken bir hata oluştu: ${errMsg}`);
      return;
    }

    setItemsByListId((prev) => ({ ...prev, [listId]: [...(prev[listId] || []), data] }));
    setNewItemInputs({ ...newItemInputs, [listId]: '' });
  };

  const toggleItem = async (listId: string, itemId: string) => {
    const listItems = itemsByListId[listId] || [];
    const item = listItems.find((i) => i.id === itemId);
    if (!item) return;

    const newStatus = !item.is_completed;

    const { error: updateError } = await supabase
      .from('list_items')
      .update({ is_completed: newStatus })
      .eq('id', itemId);

    if (updateError) {
      setError('Durum güncellenirken bir hata oluştu');
      return;
    }

    setItemsByListId((prev) => ({
      ...prev,
      [listId]: (prev[listId] || []).map((i) => i.id === itemId ? { ...i, is_completed: newStatus } : i)
    }));
  };

  const openNotePopup = (listId: string, itemId: string) => {
    const listItems = itemsByListId[listId] || [];
    const item = listItems.find((i) => i.id === itemId);
    if (item) {
      setNotePopup({ listId, itemId, notes: item.notes || '' });
      setEditNotes(item.notes || '');
    }
  };

  const saveNotes = async () => {
    if (!notePopup) return;

    const { error: updateError } = await supabase
      .from('list_items')
      .update({ notes: editNotes || null })
      .eq('id', notePopup.itemId);

    if (updateError) {
      setError('Not kaydedilirken bir hata oluştu');
      return;
    }

    setItemsByListId((prev) => ({
      ...prev,
      [notePopup.listId]: (prev[notePopup.listId] || []).map((i) => i.id === notePopup.itemId ? { ...i, notes: editNotes || null } : i)
    }));
    setNotePopup(null);
  };

  const startEditing = (item: ListItem) => {
    setEditingItemId(item.id);
    setEditingContent(item.content);
  };

  const saveEditItem = async (listId: string) => {
    if (!editingItemId || !editingContent.trim()) return;

    setSavingEdit(true);
    const { error: updateError } = await supabase
      .from('list_items')
      .update({ content: editingContent.trim() })
      .eq('id', editingItemId);

    if (updateError) {
      setError('Kalem güncellenirken bir hata oluştu');
      setSavingEdit(false);
      return;
    }

    setItemsByListId((prev) => ({
      ...prev,
      [listId]: (prev[listId] || []).map((i) => i.id === editingItemId ? { ...i, content: editingContent.trim() } : i)
    }));
    setEditingItemId(null);
    setEditingContent('');
    setSavingEdit(false);
  };

  const deleteItem = async (listId: string, itemId: string) => {
    const { error: deleteError } = await supabase
      .from('list_items')
      .delete()
      .eq('id', itemId);

    if (deleteError) {
      setError('Kalem silinirken bir hata oluştu');
      return;
    }

    setItemsByListId((prev) => ({
      ...prev,
      [listId]: (prev[listId] || []).filter((i) => i.id !== itemId)
    }));
  };

  const convertToTask = async (listId: string, item: ListItem) => {
    if (!confirm(`"${item.content}" kalemini bir göreve dönüştürmek istediğinize emin misiniz?`)) return;

    // 1. Get max priority for tasks in this project to place it at the end
    const { data: tasksData } = await supabase
      .from('tasks')
      .select('priority')
      .eq('project_id', projectId)
      .order('priority', { ascending: false })
      .limit(1);
    
    const nextPriority = tasksData && tasksData.length > 0 ? tasksData[0].priority + 1 : 1;

    // 2. Create task
    const { error: taskError } = await supabase
      .from('tasks')
      .insert({
        project_id: projectId,
        title: item.content,
        description: item.notes,
        status: 'pending',
        priority: nextPriority
      });

    if (taskError) {
      setError('Görev oluşturulurken bir hata oluştu');
      return;
    }

    // 3. Delete from checklist
    await deleteItem(listId, item.id);
    
    alert('Kalem başarıyla göreve dönüştürüldü. "İşler" sekmesinde görebilirsiniz.');
  };

  const openTablePopup = (listId: string) => {
    setTablePopup({ listId });
    setTableTitle('Excel Tablosu');
    setTablePaste('');
  };

  const addTableToList = async () => {
    if (!tablePopup) return;
    const parsedTable = parseTabularText(tablePaste);

    if (!parsedTable) {
      setError('Tablo verisi okunamadı. Excelden kopyalayıp tekrar yapıştırın.');
      return;
    }

    setAddingTable(true);
    setError('');

    const { data, error: insertError } = await supabase
      .from('list_items')
      .insert({
        list_id: tablePopup.listId,
        content: tableTitle.trim() || 'Excel Tablosu',
        notes: serializeTableToNotes(parsedTable),
      })
      .select()
      .single();

    if (insertError || !data) {
      setError('Tablo eklenirken bir hata oluştu');
      setAddingTable(false);
      return;
    }

    setItemsByListId((prev) => ({ ...prev, [tablePopup.listId]: [...(prev[tablePopup.listId] || []), data] }));
    setTablePopup(null);
    setAddingTable(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add List Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-stone-800">Kontrol Listeleri</h2>
        <button
          onClick={() => setShowAddList(true)}
          className="px-3 py-1.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Liste Ekle
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Add List Form */}
      {showAddList && (
        <form onSubmit={handleAddList} className="bg-white rounded-xl p-4 border border-stone-200">
          <div className="flex gap-3">
            <input
              type="text"
              value={newListTitle}
              onChange={(e) => setNewListTitle(e.target.value)}
              placeholder="Liste başlığı..."
              required
              disabled={addingList}
              className="flex-1 px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={addingList}
              className="px-4 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {addingList ? '...' : 'Ekle'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddList(false); setNewListTitle(''); }}
              disabled={addingList}
              className="px-4 py-2 border border-stone-300 text-stone-600 rounded-lg text-sm hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              İptal
            </button>
          </div>
        </form>
      )}

      {/* Lists */}
      {lists.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-stone-400 text-sm">Henüz liste bulunmuyor</p>
        </div>
      ) : (
        lists.map((list) => {
          const listItems = itemsByListId[list.id] || [];
          const completedCount = listItems.filter((i) => i.is_completed).length;

          return (
            <div key={list.id} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              {/* List Header */}
              <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-stone-800 text-sm">{list.title}</h3>
                  <p className="text-xs text-stone-400 mt-0.5">
                    {completedCount}/{listItems.length} tamamlandı
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openTablePopup(list.id)}
                    className="px-2.5 py-1.5 border border-stone-200 rounded-lg text-xs text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
                    title="Excel tablosu ekle"
                  >
                    <i className="ri-table-line mr-1"></i>
                    Tablo Ekle
                  </button>
                  <button
                    onClick={() => handleDeleteList(list.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>

              {/* Items */}
              <div className="px-5 py-3 space-y-2">
                {listItems.map((item) => (
                  (() => {
                    const parsedTable = parseTableFromNotes(item.notes);
                    const isTableItem = parsedTable !== null;

                    return (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 p-2.5 rounded-lg transition-colors ${
                      item.is_completed ? 'bg-stone-50' : 'hover:bg-stone-50'
                    }`}
                  >
                    <button
                      onClick={() => toggleItem(list.id, item.id)}
                      className={`w-5 h-5 flex items-center justify-center rounded border-2 mt-0.5 transition-colors cursor-pointer ${
                        item.is_completed
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-stone-300 hover:border-amber-500'
                      }`}
                    >
                      {item.is_completed && <i className="ri-check-line text-xs"></i>}
                    </button>

                    <div className="flex-1 min-w-0">
                      {editingItemId === item.id ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={editingContent}
                            onChange={(e) => setEditingContent(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditItem(list.id);
                                if (e.key === 'Escape') setEditingItemId(null);
                            }}
                            autoFocus
                            disabled={savingEdit}
                            className="flex-1 px-2 py-1 rounded border border-amber-400 text-sm focus:outline-none ring-2 ring-amber-100"
                          />
                          <button
                            onClick={() => saveEditItem(list.id)}
                            disabled={savingEdit}
                            className="text-emerald-600 hover:text-emerald-700 p-1"
                          >
                            <i className="ri-check-line font-bold"></i>
                          </button>
                          <button
                            onClick={() => setEditingItemId(null)}
                            disabled={savingEdit}
                            className="text-red-500 hover:text-red-600 p-1"
                          >
                            <i className="ri-close-line font-bold"></i>
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className={`text-sm ${item.is_completed ? 'text-stone-400 line-through' : 'text-stone-700'}`}>
                            {item.content}
                          </p>
                          {!isTableItem && item.notes && (
                            <p className="text-xs text-stone-400 mt-1 line-clamp-1">{item.notes}</p>
                          )}
                          {isTableItem && parsedTable && (
                            <div className="mt-2 overflow-x-auto border border-stone-200 rounded-lg">
                              <table className="min-w-full text-xs">
                                <thead className="bg-stone-100">
                                  <tr>
                                    {parsedTable.headers.map((header, idx) => (
                                      <th key={idx} className="px-2 py-1.5 text-left text-stone-600 border-b border-stone-200 whitespace-nowrap">
                                        {header || '-'}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {parsedTable.rows.map((row, rowIdx) => (
                                    <tr key={rowIdx} className="border-b border-stone-100 last:border-b-0">
                                      {row.map((cell, cellIdx) => (
                                        <td key={cellIdx} className="px-2 py-1.5 text-stone-700 whitespace-nowrap">
                                          {cell || '-'}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEditing(item)}
                        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors cursor-pointer"
                        title="Düzenle"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => openNotePopup(list.id, item.id)}
                        className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                          item.notes ? 'bg-amber-100 text-amber-600' : 'hover:bg-stone-100 text-stone-400'
                        }`}
                        title="Not ekle/düzenle"
                      >
                        <i className="ri-sticky-note-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => convertToTask(list.id, item)}
                        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-blue-50 text-stone-400 hover:text-blue-500 transition-colors cursor-pointer"
                        title="Göreve Dönüştür"
                      >
                        <i className="ri-share-forward-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => deleteItem(list.id, item.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                    );
                  })()
                ))}

                {/* Add Item Input */}
                <div className="flex gap-2 pt-2">
                  <input
                    type="text"
                    value={newItemInputs[list.id] || ''}
                    onChange={(e) => setNewItemInputs({ ...newItemInputs, [list.id]: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddItem(list.id)}
                    placeholder="Yeni kalem ekle..."
                    className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-stone-700 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  />
                  <button
                    onClick={() => handleAddItem(list.id)}
                    className="px-3 py-2 bg-stone-100 text-stone-600 rounded-lg text-sm hover:bg-stone-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* Note Popup */}
      {notePopup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Not Ekle</h3>
              <button
                onClick={() => setNotePopup(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-stone-500"></i>
              </button>
            </div>

            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={5}
              className="w-full px-4 py-3 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none"
              placeholder="Bu kalem hakkında notlar..."
            />

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setNotePopup(null)}
                className="flex-1 py-2.5 border border-stone-300 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                İptal
              </button>
              <button
                onClick={saveNotes}
                className="flex-1 py-2.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Table Popup */}
      {tablePopup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Excel Tablosu Ekle</h3>
              <button
                onClick={() => setTablePopup(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-stone-500"></i>
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="text"
                value={tableTitle}
                onChange={(e) => setTableTitle(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                placeholder="Tablo başlığı"
              />

              <textarea
                value={tablePaste}
                onChange={(e) => setTablePaste(e.target.value)}
                rows={10}
                className="w-full px-4 py-3 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none"
                placeholder="Exceldeki hücreleri kopyalayıp buraya yapıştırın (Ctrl+V)"
              />
              <p className="text-xs text-stone-400">
                Ilk satir baslik olarak kabul edilir. Sutunlari Exceldeki gibi tab ile ayirin.
              </p>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setTablePopup(null)}
                disabled={addingTable}
                className="flex-1 py-2.5 border border-stone-300 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Iptal
              </button>
              <button
                onClick={addTableToList}
                disabled={addingTable}
                className="flex-1 py-2.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {addingTable ? 'Ekleniyor...' : 'Tabloyu Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
